import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, apiPatch, apiPost } from "@/lib/api";
import { grInvoiceKeys } from "@/lib/query";

// ── Types ──

export type GrInvoiceStatus =
  | "pending_pm"
  | "approved"
  | "rejected"
  | "on_hold"
  | "posted_to_vista"
  | "included_in_pay_app";

export interface GrInvoiceLineSplit {
  id: string;
  grInvoiceId: string;
  commitmentLineItemId: string;
  costCodeId: string;
  amount: string;
  description: string | null;
}

export interface GrInvoiceAttachment {
  id: string;
  payAppId: string;
  payAppType: string;
  fileName: string;
  fileSize: number;
  mimeType: string | null;
  storagePath: string | null;
  uploadedBy: string | null;
  uploadedDate: string | null;
  createdAt: string;
}

export interface GrInvoice {
  id: string;
  orgId: string;
  projectId: string;
  vendorCompanyId: string | null;
  vendorNameRaw: string;
  invoiceNumber: string;
  invoiceDate: string;
  invoiceAmount: string;
  status: GrInvoiceStatus;
  billingPeriodId: string | null;
  intakeUserId: string;
  intakedAt: string;
  pmApproverId: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  holdNote: string | null;
  budgetOverrideAck: boolean;
  budgetOverrideReason: string | null;
  vistaBatchId: string | null;
  vistaPostedAt: string | null;
  vistaPostedBy: string | null;
  ownerPayAppId: string | null;
  includedInPayAppAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GrInvoiceDetail extends GrInvoice {
  splits: GrInvoiceLineSplit[];
  attachments: GrInvoiceAttachment[];
}

export interface GrSplitInput {
  commitmentLineItemId: string;
  amount: string;
  description?: string;
}

// ── List ──

export interface GrInvoiceListFilters {
  projectId?: string;
  status?: GrInvoiceStatus;
  billingPeriodId?: string;
  vendorCompanyId?: string;
  q?: string;
}

export function useGrInvoiceList(filters?: GrInvoiceListFilters) {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  if (filters?.status) params.set("status", filters.status);
  if (filters?.billingPeriodId) params.set("billingPeriodId", filters.billingPeriodId);
  if (filters?.vendorCompanyId) params.set("vendorCompanyId", filters.vendorCompanyId);
  if (filters?.q) params.set("q", filters.q);
  const qs = params.toString();

  return useQuery({
    queryKey: grInvoiceKeys.list(filters as Record<string, unknown>),
    queryFn: () => apiFetch<GrInvoice[]>(`/gr-invoices${qs ? `?${qs}` : ""}`),
  });
}

// ── Approved-but-not-posted (submit blocker for owner pay app) ──

export function useApprovedGrBlockers(projectId: string, billingPeriodId: string | null) {
  const params = new URLSearchParams({ status: "approved" });
  if (projectId) params.set("projectId", projectId);
  if (billingPeriodId) params.set("billingPeriodId", billingPeriodId);
  return useQuery({
    queryKey: grInvoiceKeys.list({ projectId, status: "approved", billingPeriodId }),
    queryFn: () => apiFetch<GrInvoice[]>(`/gr-invoices?${params.toString()}`),
    enabled: !!projectId,
  });
}

// ── Detail ──

export function useGrInvoice(id: string) {
  return useQuery({
    queryKey: grInvoiceKeys.detail(id),
    queryFn: () => apiFetch<GrInvoiceDetail>(`/gr-invoices/${id}`),
    enabled: !!id,
  });
}

// ── Pending Vista queue ──

export function usePendingVistaInvoices(filters?: { projectId?: string }) {
  const params = new URLSearchParams();
  if (filters?.projectId) params.set("projectId", filters.projectId);
  const qs = params.toString();

  return useQuery({
    queryKey: grInvoiceKeys.pendingVista(filters as Record<string, unknown>),
    queryFn: () => apiFetch<GrInvoice[]>(`/gr-invoices/pending-vista${qs ? `?${qs}` : ""}`),
  });
}

// ── Intake (multipart form upload) ──

export function useIntakeGrInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (formData: FormData) =>
      fetch("/api/gr-invoices", {
        method: "POST",
        body: formData,
        credentials: "include",
      }).then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error((body as Record<string, string>).error ?? "Upload failed");
        }
        return res.json() as Promise<{ data: GrInvoice }>;
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      toast.success("Invoice intaked — awaiting PM approval");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Approve ──

export function useApproveGr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      splits,
      budgetOverrideAck,
      budgetOverrideReason,
    }: {
      id: string;
      splits: GrSplitInput[];
      budgetOverrideAck?: boolean;
      budgetOverrideReason?: string;
    }) =>
      apiPost<{ data: GrInvoice }>(`/gr-invoices/${id}/approve`, {
        splits,
        budgetOverrideAck,
        budgetOverrideReason,
      }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.detail(id) });
      toast.success("Invoice approved");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Reject ──

export function useRejectGr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiPost<{ data: GrInvoice }>(`/gr-invoices/${id}/reject`, { reason }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.detail(id) });
      toast.success("Invoice rejected");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Hold / Unhold ──

export function useHoldGr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiPost<{ data: GrInvoice }>(`/gr-invoices/${id}/hold`, { note }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.detail(id) });
      toast.success("Invoice placed on hold");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUnholdGr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiPost<{ data: GrInvoice }>(`/gr-invoices/${id}/unhold`),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.detail(id) });
      toast.success("Invoice returned to pending");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Post to Vista ──

export function usePostToVistaGr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, vistaBatchId }: { id: string; vistaBatchId: string }) =>
      apiPost<{ data: GrInvoice }>(`/gr-invoices/${id}/post-to-vista`, { vistaBatchId }),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.detail(id) });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.pendingVista() });
      toast.success("Invoice marked as posted to Vista");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Bulk approve ──

export function useBulkApproveGr() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      items,
      budgetOverrideAck,
    }: {
      items: Array<{ id: string; splits: GrSplitInput[] }>;
      budgetOverrideAck?: boolean;
    }) =>
      apiPost<{ approved: number; errors: Array<{ id: string; error: string }> }>(
        "/gr-invoices/bulk-approve",
        { items, budgetOverrideAck },
      ),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      if (data.errors.length > 0) {
        toast.error(`${data.approved} approved, ${data.errors.length} failed`);
      } else {
        toast.success(`${data.approved} invoice${data.approved === 1 ? "" : "s"} approved`);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

// ── Update header (while pending_pm) ──

export function useUpdateGrInvoice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      data: Partial<
        Pick<
          GrInvoice,
          "vendorNameRaw" | "invoiceNumber" | "invoiceDate" | "invoiceAmount" | "billingPeriodId"
        >
      >,
    ) => apiPatch<{ data: GrInvoice }>(`/gr-invoices/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: grInvoiceKeys.all });
      qc.invalidateQueries({ queryKey: grInvoiceKeys.detail(id) });
      toast.success("Invoice updated");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
